'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  Home,
  MessageSquareText,
  BarChart3,
  Globe,
  Files,
  Activity,
  ShieldAlert,
  BookOpen,
  BookText,
  Settings2,
  User,
  Users,
  Key,
  Plug,
  Rocket,
} from 'lucide-react';
import {
  CommandPaletteShell,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from './command';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { websiteUrl } from '../../lib/appUrls';
import { consoleRoutes } from '@/lib/routes';
import { useEntitlements } from '@/hooks/api/useEntitlements';
import { useWorkspaceStore } from '@/hooks/state';

// --- Navigation index ---
//
// SCOPE, deliberately: this palette jumps to PAGES and SETTINGS, not to entities.
// It indexes no competitors, questions, topics, playbooks or source records — those are
// searched on their own list pages, each of which has a `SearchFilterBar`.
//
// That scope is why the input reads "Jump to a page or setting..." and the empty
// state says "No page matches …" and names where entity search lives. It must NOT
// say "Search…" / "No results found": typing a real competitor's name then asserts
// the workspace has no such competitor, which is false. If entity search is ever
// wired here (it needs a cross-entity backend endpoint that does not exist yet),
// widen the copy in the same commit — never before.
//
// `keywords` DO cover entity CONCEPTS ('competitors', 'personas', 'features') so
// those queries land on the page that lists them. That is navigation, not search.

interface SearchableItem {
  id: string;
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  keywords: string[];
  section: string;
  isExternal?: boolean;
  requires?: 'query-fanout' | 'managed-optimization' | 'agent-mirror';
}

const SEARCH_INDEX: SearchableItem[] = [
  // Pages
  {
    id: 'overview',
    title: 'Overview',
    href: consoleRoutes.overview,
    icon: Home,
    section: 'Pages',
    keywords: ['home', 'dashboard', 'score', 'trend', 'ai presence'],
  },
  {
    id: 'benchmark-results',
    title: 'Benchmark Results',
    href: consoleRoutes.benchmarks.results,
    icon: BarChart3,
    section: 'Pages',
    keywords: ['benchmarks', 'periods', 'scores', 'results', 'analysis', 'schedule'],
  },
  {
    id: 'questions',
    title: 'Prompts',
    href: consoleRoutes.demand.questions,
    icon: MessageSquareText,
    section: 'Pages',
    keywords: ['library', 'prompts', 'buyer questions', 'topics', 'demand'],
  },
  {
    id: 'topics',
    title: 'Topics',
    href: consoleRoutes.demand.topics,
    icon: MessageSquareText,
    section: 'Pages',
    keywords: ['topics', 'demand', 'search intent'],
  },
  {
    id: 'query-fanout',
    title: 'Query Fan Out',
    href: consoleRoutes.data.queryFanOut,
    icon: Activity,
    section: 'Pages',
    keywords: ['queries', 'query fanout', 'retrieval queries', 'execution evidence', 'search behavior'],
    requires: 'query-fanout',
  },
  {
    id: 'fact-ledger',
    title: 'Fact Ledger',
    href: consoleRoutes.knowledge.factLedger,
    icon: Files,
    section: 'Pages',
    keywords: [
      'knowledge',
      'facts',
      'approved records',
      'products',
      'capabilities',
      'buyer roles',
      'competitors',
      'themes',
    ],
  },
  {
    id: 'to-approve',
    title: 'To approve',
    href: consoleRoutes.knowledge.discoveries,
    icon: Files,
    section: 'Pages',
    keywords: ['knowledge', 'candidates', 'discoveries', 'review queue', 'facts', 'observations'],
  },
  {
    id: 'sources',
    title: 'Sources',
    href: consoleRoutes.data.sources,
    icon: Globe,
    section: 'Pages',
    keywords: ['data', 'cited sources', 'citations', 'domains', 'evidence', 'answers'],
  },
  {
    id: 'content-coverage',
    title: 'Content coverage',
    href: consoleRoutes.publish.coverage,
    icon: Files,
    section: 'Pages',
    keywords: ['content', 'content gaps', 'page types', 'coverage'],
  },
  {
    id: 'claims',
    title: 'Claims',
    href: consoleRoutes.data.answerClaims,
    icon: ShieldAlert,
    section: 'Pages',
    keywords: ['intelligence', 'answer claims', 'page claims', 'owned content', 'extracted assertions'],
  },
  {
    id: 'vocabulary',
    title: 'Vocabulary · Terms',
    href: consoleRoutes.data.vocabulary,
    icon: Files,
    section: 'Pages',
    keywords: ['data', 'language', 'terms', 'cited sources', 'coverage'],
  },
  {
    id: 'themes',
    title: 'Themes',
    href: consoleRoutes.data.themes,
    icon: ShieldAlert,
    section: 'Pages',
    keywords: ['intelligence', 'themes', 'observations', 'cited sources'],
  },
  {
    id: 'issues',
    title: 'Issues',
    href: consoleRoutes.data.issues,
    icon: ShieldAlert,
    section: 'Pages',
    keywords: ['optimize', 'misrepresentation', 'gaps', 'problems', 'errors', 'inaccuracies', 'actions'],
  },
  {
    id: 'playbooks',
    title: 'Playbooks',
    href: consoleRoutes.publish.playbooks,
    icon: BookOpen,
    section: 'Pages',
    keywords: ['optimize', 'remediation', 'recommendations', 'content strategy', 'fixes', 'actions', 'tasks'],
  },
  {
    id: 'updates',
    title: 'Updates',
    href: consoleRoutes.publish.updates,
    icon: Files,
    section: 'Pages',
    keywords: ['optimize', 'managed changes', 'approval', 'cycles', 'revisions'],
    requires: 'managed-optimization',
  },
  {
    id: 'agent-ledger',
    title: 'Agent Ledger',
    href: consoleRoutes.publish.ledger,
    icon: BookOpen,
    section: 'Pages',
    keywords: ['publish', 'corpus', 'agent versions', 'facts'],
    requires: 'agent-mirror',
  },
  {
    id: 'content-pages',
    title: 'Your Pages',
    href: consoleRoutes.publish.pages,
    icon: Files,
    section: 'Pages',
    keywords: ['publish', 'owned pages', 'agent versions', 'markdown', 'content', 'original pages'],
  },
  {
    id: 'interfaces',
    title: 'Agent interfaces',
    href: consoleRoutes.publish.interfaces,
    icon: Plug,
    section: 'Pages',
    keywords: ['publish', 'mcp', 'webmcp', 'feed', 'customer domain'],
    requires: 'agent-mirror',
  },
  {
    id: 'content-access',
    title: 'Publish access',
    href: consoleRoutes.publish.access,
    icon: Files,
    section: 'Pages',
    keywords: ['publish', 'robots', 'llms.txt', 'access', 'reachability', 'negotiation'],
  },
  {
    id: 'content-delivery',
    title: 'Publish delivery',
    href: consoleRoutes.publish.delivery,
    icon: Files,
    section: 'Pages',
    keywords: ['publish', 'agent versions', 'cloudflare', 'worker', 'deployment', 'delivery'],
    requires: 'agent-mirror',
  },
  {
    id: 'agent-activity',
    title: 'Agent activity',
    href: consoleRoutes.traffic.agents,
    icon: Activity,
    section: 'Pages',
    keywords: ['verified agents', 'crawler traffic', 'cloudflare', 'requests'],
  },
  {
    id: 'ai-referrals',
    title: 'AI referrals',
    href: consoleRoutes.traffic.referrals,
    icon: Activity,
    section: 'Pages',
    keywords: ['visits', 'analytics', 'ga4', 'traffic', 'referrals'],
  },
  {
    id: 'agent-tool-calls',
    title: 'Agent tool calls',
    href: consoleRoutes.traffic.tools,
    icon: Activity,
    section: 'Pages',
    keywords: ['traffic', 'mcp', 'tools', 'calls'],
    requires: 'agent-mirror',
  },
  // Settings
  {
    id: 'settings-workspace',
    title: 'Workspace settings',
    href: consoleRoutes.settings.workspace,
    icon: Settings2,
    section: 'Settings',
    keywords: ['workspace', 'name', 'slug', 'configuration'],
  },
  {
    id: 'settings-team',
    title: 'Team settings',
    href: consoleRoutes.settings.team,
    icon: Users,
    section: 'Settings',
    keywords: ['members', 'invite', 'roles', 'permissions'],
  },
  {
    id: 'settings-integrations',
    title: 'Integrations',
    href: consoleRoutes.settings.integrations,
    icon: Plug,
    section: 'Settings',
    keywords: ['connections', 'gsc', 'ga4', 'google', 'notion', 'search console', 'analytics'],
  },
  {
    id: 'settings-api',
    title: 'Workspace API & MCP',
    href: consoleRoutes.settings.integrations,
    icon: Key,
    section: 'Settings',
    keywords: ['tokens', 'api', 'keys', 'mcp', 'authentication', 'access'],
  },
  {
    id: 'settings-profile',
    title: 'Profile',
    href: consoleRoutes.settings.profile,
    icon: User,
    section: 'Settings',
    keywords: ['account', 'user', 'name', 'email', 'profile'],
  },
  // Docs
  {
    id: 'docs',
    title: 'Documentation',
    href: websiteUrl('/docs/'),
    icon: BookText,
    section: 'Pages',
    keywords: ['docs', 'help', 'guide', 'documentation'],
    isExternal: true,
  },
];

/**
 * What a LOCKED workspace may still jump to: the wizard, Settings, and Docs.
 *
 * A palette entry that silently redirects is worse than one that is not offered,
 * and offering `/sources` here would contradict the greyed sidebar three inches
 * to the left. Same source of truth as that sidebar — the `locked` prop, from
 * `lib/consoleLock`.
 */
const REACHABLE_WHILE_LOCKED = (item: SearchableItem): boolean => item.section === 'Settings' || item.id === 'docs';

const ONBOARDING_ITEM: SearchableItem = {
  id: 'onboarding',
  title: 'Setup',
  href: consoleRoutes.setup,
  icon: Rocket,
  section: 'Pages',
  keywords: ['setup', 'set up', 'onboarding', 'get started', 'research', 'company', 'domain'],
};

function matchesQuery(item: SearchableItem, q: string): boolean {
  const lower = q.toLowerCase();
  if (item.title.toLowerCase().includes(lower)) return true;
  return item.keywords.some((kw) => kw.includes(lower));
}

// --- Recent items (localStorage) ---

interface RecentItem {
  type: 'page';
  id: string;
  title: string;
  route: string;
}

const RECENT_KEY = 'knitknot_recent_items';
const MAX_RECENT = 8;

function getRecentItems(): RecentItem[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addToRecent(item: RecentItem) {
  const items = getRecentItems().filter((i) => i.id !== item.id);
  items.unshift(item);
  localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, MAX_RECENT)));
}

// --- Component ---

export const CommandPalette: React.FC<{ locked?: boolean }> = ({ locked = false }) => {
  const { isOpen, setIsOpen } = useCommandPalette();
  const navigate = useNavigate();
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const { queryFanoutEnabled, managedOptimizationEnabled, agentMirrorEnabled } = useEntitlements(workspaceId);

  const [query, setQuery] = useState('');

  const [recentItems] = useState(getRecentItems);

  const index = useMemo(() => {
    const entitled = SEARCH_INDEX.filter((item) => {
      if (item.requires === 'query-fanout') return queryFanoutEnabled;
      if (item.requires === 'managed-optimization') return managedOptimizationEnabled;
      if (item.requires === 'agent-mirror') return agentMirrorEnabled;
      return true;
    });
    return locked ? [ONBOARDING_ITEM, ...entitled.filter(REACHABLE_WHILE_LOCKED)] : entitled;
  }, [agentMirrorEnabled, locked, managedOptimizationEnabled, queryFanoutEnabled]);

  const filtered = useMemo(() => {
    if (!query) return [];
    return index.filter((item) => matchesQuery(item, query));
  }, [query, index]);

  const currentRecentItems = useMemo(
    () => recentItems.filter((recent) => index.some((item) => item.id === recent.id && item.href === recent.route)),
    [index, recentItems],
  );

  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchableItem[]> = {};
    for (const item of filtered) {
      (groups[item.section] ??= []).push(item);
    }
    return groups;
  }, [filtered]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) setQuery('');
    },
    [setIsOpen],
  );

  const handleSelect = useCallback(
    (item: SearchableItem) => {
      addToRecent({ type: 'page', id: item.id, title: item.title, route: item.href });
      handleOpenChange(false);
      if (item.isExternal) {
        window.open(item.href, '_blank');
      } else {
        navigate(item.href);
      }
    },
    [navigate, handleOpenChange],
  );

  const handleRecentSelect = useCallback(
    (item: RecentItem) => {
      addToRecent(item);
      handleOpenChange(false);
      navigate(item.route);
    },
    [navigate, handleOpenChange],
  );

  const hasResults = filtered.length > 0;
  const showRecent = !query && currentRecentItems.length > 0;
  const sectionKeys = Object.keys(groupedResults);

  return (
    <CommandPaletteShell open={isOpen} onOpenChange={handleOpenChange} title="Jump to a page or setting">
      <CommandInput placeholder="Jump to a page or setting..." value={query} onValueChange={setQuery} />
      <CommandList className="max-h-[360px]">
        {query && !hasResults && (
          <CommandEmpty>
            <span className="block">No page matches &ldquo;{query}&rdquo;.</span>
            <span className="mt-1 block text-xs text-ink-supporting">
              Competitors, prompts, sources and playbooks are searchable on their own pages.
            </span>
          </CommandEmpty>
        )}

        {showRecent && (
          <CommandGroup heading="Recent">
            {currentRecentItems.map((item) => (
              <CommandItem key={item.id} value={`recent-${item.id}`} onSelect={() => handleRecentSelect(item)}>
                <Clock className="mr-2 h-4 w-4 text-ink-supporting" />
                <span className="flex-1 truncate">{item.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {sectionKeys.map((section, idx) => (
          <React.Fragment key={section}>
            {(idx > 0 || showRecent) && <CommandSeparator />}
            <CommandGroup heading={section}>
              {groupedResults[section].map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem key={item.id} value={`item-${item.id}`} onSelect={() => handleSelect(item)}>
                    {Icon && <Icon className="mr-2 h-4 w-4 text-ink-supporting" />}
                    <span className="flex-1">{item.title}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>

      <div className="flex items-center gap-3 border-t px-3 py-2 text-xs text-ink-supporting">
        <span>
          <kbd className="rounded border px-1 font-mono">↑↓</kbd> Navigate
        </span>
        <span>
          <kbd className="rounded border px-1 font-mono">⏎</kbd> Open
        </span>
        <span>
          <kbd className="rounded border px-1 font-mono">esc</kbd> Close
        </span>
      </div>
    </CommandPaletteShell>
  );
};
